import { useState } from "react";
import { Alert, Box, Collapse, Stack, Tooltip, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BookingType } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import {
  formatFilterCompatibilityPromptLabel,
  type FilterCompatibility
} from "../../../../../../features/Booking-Form/functions/booking-filter-compatibility.functions";
import { buildFilterCompatibilityBreakdownRows } from "../../../../../../features/Booking-Form/functions/booking-filter-compatibility-breakdown.functions";
import { useRemoteFetchBookingInventoryFilterOptions } from "../../../../../../hooks/Remote/Booking/useRemoteFetchBookingInventoryFilterOptions";

type DetailRowProps = {
  name: string;
  detail: string;
};

function FilterCompatibilityDetailRow({ name, detail }: Readonly<DetailRowProps>) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="subtitle2" component="p" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {name}
      </Typography>
      <Typography variant="body2" component="p" sx={{ opacity: 0.78, lineHeight: 1.45 }}>
        {detail}
      </Typography>
    </Stack>
  );
}

const FILTER_COMPATIBILITY_DETAILS_MAX_HEIGHT = 220;

type Props = {
  compatibility: FilterCompatibility | undefined;
  bookingType: BookingType | null | undefined;
};

export function FilterCompatibilityBreakdown({ compatibility, bookingType }: Readonly<Props>) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { data: catalogs } = useRemoteFetchBookingInventoryFilterOptions(bookingType ?? undefined, {
    enabled: Boolean(bookingType && compatibility)
  });

  const label = formatFilterCompatibilityPromptLabel(compatibility, t);
  if (!label) {
    return null;
  }

  const rows = buildFilterCompatibilityBreakdownRows(
    compatibility,
    catalogs,
    bookingType,
    i18n.language,
    t
  );
  const canExpand = rows.length > 0;
  const expandHint = expanded
    ? t("_booking_filterCompatibility_clickToHide")
    : t("_booking_filterCompatibility_clickForMoreInfo");

  return (
    <Alert
      severity="info"
      data-testid="filter-compatibility-breakdown"
      sx={{
        alignItems: "flex-start",
        "& .MuiAlert-message": {
          flex: 1,
          minWidth: 0,
          width: "100%"
        }
      }}
    >
      {canExpand ? (
        <Tooltip title={expandHint}>
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded(isOpen => !isOpen)}
            aria-expanded={expanded}
            aria-controls="filter-compatibility-details"
            aria-label={`${label}. ${expandHint}`}
            data-testid="filter-compatibility-summary"
            sx={{
              all: "unset",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
              cursor: "pointer"
            }}
          >
            <Typography fontWeight={600}>{label}</Typography>
            <ExpandMore
              fontSize="small"
              aria-hidden
              sx={{
                flexShrink: 0,
                ml: "auto",
                transform: expanded ? "rotate(180deg)" : "none"
              }}
            />
          </Box>
        </Tooltip>
      ) : (
        <Typography fontWeight={600}>{label}</Typography>
      )}
      <Collapse in={expanded} timeout={0} unmountOnExit sx={{ width: "100%" }}>
        {canExpand && (
          <Stack
            id="filter-compatibility-details"
            data-testid="filter-compatibility-details"
            role="region"
            aria-label={t("_booking_filterCompatibility_detailsRegion")}
            tabIndex={0}
            spacing={1.25}
            sx={{
              mt: 1.25,
              width: "100%",
              maxHeight: FILTER_COMPATIBILITY_DETAILS_MAX_HEIGHT,
              overflowY: "auto",
              overscrollBehavior: "contain",
              pr: 0.5
            }}
          >
            {rows.map(row => (
              <FilterCompatibilityDetailRow key={row.key} name={row.name} detail={row.detail} />
            ))}
          </Stack>
        )}
      </Collapse>
    </Alert>
  );
}
